/**
 * Typed client for the escalation buyer-detail endpoint (ADR-0094
 * Slice 3, built in parallel):
 *
 *   GET /v1/escalations/:id/buyer-detail  (owner/admin/reviewer)
 *
 * For a PENDING escalation an operator reveals who the held order is
 * for before deciding approve/reject. The reveal is access-logged
 * server-side (the same posture as the audit buyer-detail decrypt).
 *
 * Response shapes:
 *
 *   - 200 captured     — `{ buyer: BuyerDetailCanonical }`
 *   - 200 unavailable  — `{ unavailable: true, reason:
 *                          'buyer_absent' | 'encrypter_unavailable'
 *                        | 'payload_too_large' | 'vault_error' }`
 *   - 401 / 403        — session expired / role gate (viewer)
 *   - 404              — escalation missing / RLS-filtered
 *   - 429 / 5xx        — surfaced through the http-client's ApiError
 *
 * Wire format is **snake_case** (CLAUDE.md §3). The result is collapsed
 * into the SAME discriminated union the audit buyer-detail panel uses
 * (`BuyerDetailFetchResult`) so `EscalationBuyerReveal` reuses the
 * existing `BuyerDetailPanel` visual states verbatim — mapping this
 * endpoint's `unavailable.reason` values onto the panel's
 * `BuyerDetailUnavailableReason` union.
 */

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';
import type {
  BuyerDetailCanonical,
  BuyerDetailFetchResult,
  BuyerDetailUnavailableReason,
} from './audit-buyer-detail.js';

/** The `reason` discriminant the escalation endpoint returns on the
 *  `{ unavailable: true }` branch. */
export type EscalationBuyerUnavailableReason =
  | 'buyer_absent'
  | 'encrypter_unavailable'
  | 'payload_too_large'
  | 'vault_error';

/* ---------- internal wire shapes (snake_case from the server) ---------- */

interface CapturedWire {
  readonly buyer?: BuyerDetailCanonical;
}

interface UnavailableWire {
  readonly unavailable?: unknown;
  readonly reason?: unknown;
}

/* --------------------------------------------------------------------- */

/**
 * GET /v1/escalations/:id/buyer-detail.
 *
 * Returns the shared `BuyerDetailFetchResult` union so the reveal panel
 * reuses `BuyerDetailPanel`'s renderers. There is no `purged` state for
 * a pending escalation (the held order has not aged out of retention),
 * so this client only emits `captured` / `not_captured` / `forbidden` /
 * `error`.
 */
export async function fetchEscalationBuyerDetail(
  http: HttpClient,
  escalation_id: string,
): Promise<BuyerDetailFetchResult> {
  try {
    const res = await http.request<CapturedWire & UnavailableWire>({
      method: 'GET',
      path: `/v1/escalations/${encodeURIComponent(escalation_id)}/buyer-detail`,
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    // The `{ unavailable: true }` branch is a 200 with no buyer block —
    // distinct from the audit endpoint's 404. Map its reason onto the
    // panel's union so the shared renderer shows the right copy.
    if (res.unavailable === true) {
      return { kind: 'not_captured', reason: mapUnavailableReason(res.reason) };
    }
    if (res.buyer !== undefined && res.buyer !== null) {
      return { kind: 'captured', buyer: res.buyer };
    }
    return { kind: 'error', message: 'Unexpected response shape.' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') {
        // A 404 here means the escalation row is gone (resolved/RLS) —
        // surface the most-generic "no buyer fields" copy.
        return { kind: 'not_captured', reason: 'buyer_absent_at_mandate' };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'error',
        message: "You've requested buyer detail too often. Wait a minute and try again.",
      };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/* ---------- helpers ---------- */

/**
 * Map the escalation endpoint's `unavailable.reason` onto the audit
 * panel's `BuyerDetailUnavailableReason`. The escalation endpoint uses
 * `buyer_absent` (vs the audit `buyer_absent_at_mandate`) and adds
 * `vault_error`, which has no panel-specific copy — fold it into
 * `encrypter_unavailable` (the "capture pipeline didn't deliver" copy
 * is the closest operator-facing meaning). Unknown values collapse to
 * the generic absent copy.
 */
function mapUnavailableReason(raw: unknown): BuyerDetailUnavailableReason {
  switch (raw) {
    case 'buyer_absent':
      return 'buyer_absent_at_mandate';
    case 'encrypter_unavailable':
    case 'vault_error':
      return 'encrypter_unavailable';
    case 'payload_too_large':
      return 'payload_too_large';
    default:
      return 'buyer_absent_at_mandate';
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'rate_limited':
      return "You've requested buyer detail too often. Wait a minute and try again.";
    case 'service_unavailable':
      return 'Buyer detail service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
